import { forwardRef } from 'react';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material/styles';
import TruncatedText from 'src/components/Common/TruncatedText';

export type CustomSnackbarVariant = 'success' | 'error' | 'warning';

function getVariantIcon(variant: CustomSnackbarVariant) {
  switch (variant) {
    case 'success':
      return NotificationsNoneIcon;
    case 'error':
      return WarningAmberIcon;
    case 'warning':
      return ErrorOutlineIcon;
    default:
      return NotificationsNoneIcon;
  }
}

type CustomSnackbarProps = {
  message: string;
  variant: CustomSnackbarVariant;
  onClose?: () => void;
};

export const CustomSnackbarContent = forwardRef<
  HTMLDivElement,
  CustomSnackbarProps
>(({ message, variant, onClose }, ref) => {
  const theme = useTheme();
  // Colores fijos por requerimiento de diseño
  const COLORS: Record<CustomSnackbarVariant, { bg: string; fg: string }> = {
    success: { bg: '#ECFBE6', fg: '#3BB900' },
    error: { bg: '#FFE8EC', fg: '#FF1A43' },
    warning: { bg: '#FFF6E8', fg: '#FFA31A' }
  };
  const bgColor = COLORS[variant]?.bg || theme.palette.background.paper;
  const textColor = COLORS[variant]?.fg || theme.palette.text.primary;
  const Icon = getVariantIcon(variant);

  return (
    <Box
      ref={ref}
      role="alert"
      aria-live="polite"
      sx={{
        backgroundColor: bgColor,
        color: textColor,
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: 620,
        boxSizing: 'border-box',
        p: 1,
        pr: 0.5
      }}
    >
      <Box ml={1} mr={1} display="flex" alignItems="center">
        <Icon sx={{ color: textColor, mr: 1 }} />
      </Box>
      <Box flex={1} minWidth={0} mr={1}>
        <TruncatedText
          text={message}
          maxWidth={'100%'}
          sx={{ color: textColor, fontWeight: 600 }}
          variant="subtitle2"
        />
      </Box>
      <Box>
        <IconButton size="small" color="inherit" onClick={onClose}>
          <CloseIcon sx={{ color: textColor, fontSize: '18px' }} />
        </IconButton>
      </Box>
    </Box>
  );
});

export default CustomSnackbarContent;
